import { useState } from 'react';
import { FaThumbtack } from '../ui/FaIcons';
import { Pencil, Trash2, Reply, Check, CheckCheck } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature } from '../../services/auth/permissions';
import { asCollaboratorRole } from '../../services/auth/permissions';
import type { Message } from '../../hooks/queries/useMessaging';
import type { MessageAttachment } from '../../services/messaging/messageAttachmentService';
import type { MessageReaction } from '../../services/messaging/messageReactionService';
import { MessageAttachmentList } from './MessageAttachmentList';
import { MessageBody } from './MessageBody';
import { MessageReactionBar } from './MessageReactionBar';
import { MessageReactionPills } from './MessageReactionPills';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { isGifMessageUrl } from '../../lib/gifSearch';

interface MessageItemProps {
  message: Message;
  showAuthor: boolean;
  onDelete: (id: string) => void;
  onEdit: (id: string, currentBody: string) => void;
  onReply: (message: Message) => void;
  /** Épingle ou désépingle le message. Absent : pas d'action d'épinglage. */
  onTogglePin?: (message: Message) => void;
  showReadReceipt?: boolean;
  dmOtherLastReadAt?: string | null;
  attachments?: MessageAttachment[];
  onDeleteAttachment?: (att: MessageAttachment) => void;
  reactions?: MessageReaction[];
  onToggleReaction?: (messageId: string, emoji: string, hasReacted: boolean) => void;
  parent?: Message | null;
  onJumpToMessage?: (messageId: string) => void;
  /** Appelé quand un média (GIF/image) du message finit de charger — sert à
   *  recaler le scroll en bas, la hauteur du média n'étant connue qu'après chargement. */
  onMediaLoad?: () => void;
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MessageItem({ message, showAuthor, onDelete, onEdit, onReply, onTogglePin, showReadReceipt = false, dmOtherLastReadAt = null, attachments = [], onDeleteAttachment, reactions = [], onToggleReaction, parent, onJumpToMessage, onMediaLoad }: MessageItemProps) {

  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function closeMenu() {
    setMenuOpen(false);
    setConfirmDelete(false);
  }

  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);

  const isOwn = collaborator?.id === message.authorId;
  const canDeleteAny = canAccessFeature(role, 'messaging_delete_any');
  const canDelete = isOwn && canAccessFeature(role, 'messaging_delete_own');
  const canEdit = isOwn;

  const isDeleted = !!message.deletedAt;

  const authorName = message.author?.full_name ?? 'Utilisateur';
  const authorInitial = authorName.charAt(0).toUpperCase();

  return (
    <div
      id={message.id}
      className={cn(
        'flex gap-2 px-4 py-0.5 transition-colors',
        showAuthor && 'mt-3',
        isOwn ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      {/* Avatar — autres uniquement */}
      {!isOwn && (
        <div className="w-12 flex-shrink-0 flex flex-col items-center mt-0.5">
          {showAuthor && (
            message.author?.avatar_url ? (
              <CollaboratorLink collaboratorId={message.authorId} title={`Voir le profil de ${authorName}`}>
                <img
                  src={message.author.avatar_url}
                  alt={authorName}
                  className="w-12 h-12 rounded-full object-cover"
                />
              </CollaboratorLink>
            ) : (
              <CollaboratorLink collaboratorId={message.authorId} title={`Voir le profil de ${authorName}`}>
                <span className="w-12 h-12 rounded-full bg-cime-primary text-white flex items-center justify-center text-sm font-medium">
                  {authorInitial}
                </span>
              </CollaboratorLink>
            )
          )}
        </div>
      )}

      {/* Bubble + meta */}
      <div className={cn('flex flex-col max-w-[70%]', isOwn ? 'items-end' : 'items-start')}>

        <div
          className="group relative flex items-start gap-1.5"
          onContextMenu={(e) => {
            if (isDeleted) return;
            e.preventDefault();
            setConfirmDelete(false);
            setMenuOpen(true);
          }}
        >
          {/* Menu contextuel (clic droit) : emotes + actions */}
          {!isDeleted && menuOpen && onToggleReaction && (
            <MessageReactionBar
              isOwn={isOwn}
              onClose={closeMenu}
              onSelect={(emoji) => {
                const hasReacted = reactions.some(
                  (r) => r.emoji === emoji && r.collaborator_id === collaborator?.id,
                );
                onToggleReaction(message.id, emoji, hasReacted);
                closeMenu();
              }}
              actions={
                confirmDelete ? (
                  <>
                    <span className="text-[11px] text-cime-text-muted px-1 whitespace-nowrap">
                      Supprimer ?
                    </span>
                    <button
                      type="button"
                      onClick={() => { onDelete(message.id); closeMenu(); }}
                      className="px-1.5 py-0.5 rounded text-[11px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Oui
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className="px-1.5 py-0.5 rounded text-[11px] text-cime-text-muted hover:bg-cime-surface transition-colors"
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => { onReply(message); closeMenu(); }}
                      className="p-1 rounded hover:bg-cime-sidebar-active-bg text-cime-text-muted hover:text-cime-text-primary transition-colors"
                      aria-label="Répondre"
                    >
                      <Reply className="w-4 h-4" />
                    </button>

                    {onTogglePin && (
                      <button
                        type="button"
                        onClick={() => { onTogglePin(message); closeMenu(); }}
                        className={cn(
                          'p-1 rounded hover:bg-cime-sidebar-active-bg transition-colors',
                          message.pinnedAt ? 'text-cime-gold-text' : 'text-cime-text-muted hover:text-cime-text-primary',
                        )}
                        aria-label={message.pinnedAt ? 'Désépingler' : 'Épingler'}
                        title={message.pinnedAt ? 'Désépingler' : 'Épingler'}
                      >
                        <FaThumbtack className="w-4 h-4" />
                      </button>
                    )}

                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => { onEdit(message.id, message.body); closeMenu(); }}
                        className="p-1 rounded hover:bg-cime-sidebar-active-bg text-cime-text-muted hover:text-cime-text-primary transition-colors"
                        aria-label="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}

                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(true)}
                        className="p-1 rounded hover:bg-red-50 text-cime-text-muted hover:text-red-500 transition-colors"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )
              }
            />
          )}

          {/* Bubble */}
          {isDeleted ? (
            <p className={cn(
              'text-xs italic px-3 py-2 rounded-2xl border',
              'text-cime-text-muted border-cime-border bg-transparent',
            )}>
              Message supprimé
            </p>
          ) : isGifMessageUrl(message.body) ? (
            <img
              src={message.body}
              alt="GIF"
              className="max-h-48 max-w-xs rounded-2xl object-cover"
              loading="lazy"
              onLoad={onMediaLoad}
            />
          ) : message.body.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {message.parentId && (
                <button
                  type="button"
                  onClick={() => parent && onJumpToMessage?.(parent.id)}
                  disabled={!parent}
                  className={cn(
                    'flex items-stretch gap-1.5 text-left text-[11px] rounded-md overflow-hidden',
                    'transition-opacity',
                    parent && 'hover:opacity-80 cursor-pointer',
                    !parent && 'opacity-60',
                  )}
                  title={parent ? 'Aller au message' : 'Message indisponible'}
                >
                  <span className="w-0.5 bg-cime-primary rounded-full flex-shrink-0" />
                  <span className="flex flex-col py-0.5 min-w-0">
                    <span className="font-medium text-cime-text-muted truncate">
                      {parent?.author?.full_name ?? 'Message'}
                    </span>
                    <span className="text-cime-text-muted truncate max-w-[260px]">
                      {parent
                        ? (parent.deletedAt
                            ? 'Message supprimé'
                            : parent.body.slice(0, 80) + (parent.body.length > 80 ? '…' : ''))
                        : 'Message indisponible'}
                    </span>
                  </span>
                </button>
              )}
              <div className={cn(
                'px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words cursor-pointer',
                'transition-shadow group-hover:shadow-md',
                isOwn
                  ? 'bg-cime-primary text-white rounded-br-sm'
                  : 'bg-cime-surface text-cime-text-primary border border-cime-border rounded-bl-sm',
              )}>
                <MessageBody body={message.body} isOwn={isOwn} />
                {message.isEdited && (
                  <span className={cn('text-[10px] ml-1', isOwn ? 'text-white/60' : 'text-cime-text-muted')}>
                    (modifié)
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {!isDeleted && attachments.length > 0 && (
          <MessageAttachmentList
            attachments={attachments}
            isOwn={isOwn}
            currentCollaboratorId={collaborator?.id ?? null}
            canDeleteAny={canDeleteAny}
            onDelete={onDeleteAttachment}
            onMediaLoad={onMediaLoad}
          />
        )}

        {!isDeleted && reactions.length > 0 && onToggleReaction && (
          <MessageReactionPills
            reactions={reactions}
            currentCollaboratorId={collaborator?.id ?? null}
            onToggle={(emoji, hasReacted) => onToggleReaction(message.id, emoji, hasReacted)}
            isOwn={isOwn}
          />
        )}

        {/* Heure d'envoi (tous les messages) + accusé de lecture (mes messages, DM uniquement) */}
        {(showAuthor || (isOwn && showReadReceipt)) && (
          <div className={cn('flex items-center gap-1 mt-0.5 px-1', isOwn && 'justify-end')}>
            {showAuthor && (
              <time dateTime={message.createdAt} className="text-[10px] text-cime-text-muted">
                {formatTime(message.createdAt)}
              </time>
            )}
            {isOwn && showReadReceipt && !isDeleted && (
              dmOtherLastReadAt && new Date(dmOtherLastReadAt) >= new Date(message.createdAt) ? (
                <span className="flex items-center gap-0.5 text-[10px] text-cime-gold" title="Lu">
                  <CheckCheck className="w-3 h-3" />
                  <span>Lu</span>
                </span>
              ) : (
                <span className="flex items-center text-[10px] text-cime-text-muted" title="Envoyé">
                  <Check className="w-3 h-3" />
                </span>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
